import { hide, show, textToHTML } from "../domUtils";
import { getTemplate } from "./graphView_html";
import {
    AUSTRALIAN_LOG_TYPES_GRAPH_ORDER,
    getGraphHeightByWidth,
    hosGraphConstructor,
    hosGraphConstructorStrings,
    type IGraphData,
    type IGraphLog,
    STANDARD_STATUS_LOG_TYPES_GRAPH_ORDER
} from "../hosGraphConstructor";
import { compareDates } from "../rules/utils";
import { ALL_AUSTRALIAN_LOG_TYPES, IValidation, STATUS_LOG_TYPES } from "../rules/helpers";
import type { IViewSectionData } from "../interfaces/viewSectionData";

interface ILocalizeMethods {
    toLocalDateTime: (date: Date | string, timeZoneId?: string) => Date;
    shortTimeFormat?: string;
    parseDateToString: (date: Date, format: string) => string;
    translateKey: (s: string) => string;
}

export class GraphView {
    private isGraphRendered = false;
    private view: HTMLElement | undefined;
    private isExpanded = false;
    private readonly placeholderContainer: HTMLElement;
    private readonly wrapperContainer: HTMLElement;
    private readonly graphContainer: HTMLElement;

    private getStatuses() {
        return this.isEwd ? [...AUSTRALIAN_LOG_TYPES_GRAPH_ORDER] : [...STANDARD_STATUS_LOG_TYPES_GRAPH_ORDER];
    }

    private toggleGraph(isShow: boolean) {
        if (!this.view) {
            return;
        }
        if (isShow) {
            hide(this.placeholderContainer);
            hide(this.wrapperContainer);
            show(this.graphContainer);
        } else {
            show(this.placeholderContainer);
            show(this.wrapperContainer);
            hide(this.graphContainer);
        }
    }

    private renderGraph() {
        let dailyLogs = (this.dayData.graphLogs || []).sort((a, b) => compareDates(b.dateTime, a.dateTime));
        let graphData: IGraphData = {
            data: dailyLogs as IGraphLog[],
            fromDate: this.dayData.dateTime,
            toDate: this.dayData.endDateTime,
            driverTimeZone: this.driverTimezone
        };

        for (let i = 0; i < dailyLogs.length; i++) {
            let currentLog = dailyLogs[i];
            if (currentLog.modifications && currentLog.modifications.length > 0) {
                dailyLogs[i] = { ...currentLog };
                dailyLogs[i]._isEdited = true;
            }
        }
        hosGraphConstructor().createGraph(
            this.graphContainer,
            graphData,
            {
                fontBold: true,
                fontScale: 6.5,
                includeViolations: true,
                includeBreaks: (this.dayData.breaks || []).length > 0,
                isEwd: this.isEwd,
                includeEdited: !this.isEwd,
                includePcYm: !this.isEwd,
                includeVerified: !this.isEwd,
                includeWorkExemption: this.isEwd,
                shortTimeFormat: this.localizeMethods.shortTimeFormat,
                getLocalDateTime: this.localizeMethods.toLocalDateTime,
                parseDateToString: this.localizeMethods.parseDateToString,
                translate: this.translate,
                showLegend: false,
                containerWidth: this.containerWidth,
                getBarDescriptorId: this.getLogIdAttr,
                statuses: this.getStatuses(),
                allStatuses: this.isEwd ? [...ALL_AUSTRALIAN_LOG_TYPES] : [...STATUS_LOG_TYPES]
            },
            (id: string) => {
                this.navigateToLog(id);
            },
            this.logValidation.violations,
            this.dayData.breaks
        );
        this.isGraphRendered = true;
    }

    constructor(
        private readonly container: HTMLElement,
        private readonly translate: (s: string) => string,
        private readonly navigateToLog: (logId: string) => void,
        private readonly dayData: IViewSectionData,
        private readonly logValidation: IValidation,
        private readonly localizeMethods: ILocalizeMethods,
        private readonly driverTimezone: string,
        private readonly getLogIdAttr: (id: string, isPlaceholder: boolean) => string,
        private readonly containerWidth?: number,
        private readonly isEwd?: boolean
    ) {
        const view = textToHTML(getTemplate(translate, this.isEwd))[0];
        this.view = view;

        this.container.appendChild(view);
        this.placeholderContainer = view.querySelector(".graphView__placeholder") as HTMLElement;
        this.wrapperContainer = view.querySelector(".graphView__wrapper") as HTMLElement;
        this.graphContainer = view.querySelector(".graphRenderContainer") as HTMLElement;
        this.setHeightByWidth(containerWidth || this.graphContainer.clientWidth);
        this.isGraphRendered = false;
    }

    render() {
        if (!this.view) {
            return;
        }
        if (!this.isGraphRendered) {
            this.toggleGraph(true);
            this.renderGraph();
        }
    }

    getGraphWidth() {
        return this.graphContainer ? this.graphContainer.clientWidth : undefined;
    }

    setHeightByWidth(width: number) {
        if (!this.wrapperContainer || !this.graphContainer) {
            return;
        }
        let height = `${this.view ? getGraphHeightByWidth(width, this.getStatuses(), true) : 0}px`;
        this.wrapperContainer.style.height = height;
        this.graphContainer.style.height = height;
    }

    toggle(state?: boolean, isManual = false) {
        let newState = typeof state === "boolean" ? state : !this.isExpanded;
        newState ? this.expand() : this.collapse();
        if (isManual && newState) {
            this.render();
            this.view && this.view.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }

    expand() {
        if (this.view) {
            show(this.view);
            this.view.classList.add("expanded");
            this.isExpanded = true;
        }
    }

    collapse() {
        if (this.view) {
            hide(this.view);
            this.view.classList.remove("expanded");
            this.isExpanded = false;
        }
    }

    destroy() {
        this.view = undefined;
        this.isGraphRendered = false;
    }

    get element() {
        return this.view;
    }

    get visible() {
        return this.isExpanded;
    }

    get expanded() {
        return this.isExpanded;
    }

    static translate(): string[] {
        return ["Rendering graph...", ...hosGraphConstructorStrings];
    }
}
